import { useCallback, useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { CATEGORIES } from "../../components/DeskLayout";
import { toast } from "sonner";
import { Plus, BookOpen, Pencil, Trash2, Search } from "lucide-react";

const inputCls = "bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";
const EMPTY = { title: "", body: "", category: "Other", published: true };

export default function KnowledgeBase() {
  const { user } = useAuth();
  const isAgent = user?.role === "hd_admin" || user?.role === "hd_agent" || user?.role === "super_admin";
  const [articles, setArticles] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [view, setView] = useState(null);
  const [editor, setEditor] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (category) params.set("category", category);
    api.get(`/helpdesk/kb?${params}`).then((r) => setArticles(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [q, category]);
  useEffect(() => { load(); }, [load]);

  const openArticle = async (a) => {
    const { data } = await api.get(`/helpdesk/kb/${a.id}`);
    setView(data);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editor.id) await api.put(`/helpdesk/kb/${editor.id}`, editor);
      else await api.post("/helpdesk/kb", editor);
      toast.success("Article saved");
      setEditor(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete article "${a.title}"?`)) return;
    try { await api.delete(`/helpdesk/kb/${a.id}`); toast.success("Article deleted"); setView(null); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="desk-kb-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold font-heading">Knowledge Base</h1>
        {isAgent && (
          <button data-testid="new-article-button" onClick={() => setEditor({ ...EMPTY })}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">
            <Plus size={15} /> New article
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-2.5 text-neutral-400" />
          <input data-testid="kb-search" placeholder="Search articles..." value={q} onChange={(e) => setQ(e.target.value)} className={inputCls + " pl-9 w-72"} />
        </div>
        <select data-testid="kb-category-filter" value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {articles.length === 0 ? (
        <div className="border border-dashed border-neutral-300 rounded-2xl p-12 text-center text-neutral-400 text-sm" data-testid="kb-empty">No articles yet.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {articles.map((a) => (
            <button key={a.id} data-testid={`kb-article-${a.id}`} onClick={() => openArticle(a)}
              className="text-left bg-white border border-neutral-200 rounded-2xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all">
              <BookOpen size={17} className="text-sky-600" />
              <h3 className="font-semibold font-heading mt-3 line-clamp-2">{a.title}</h3>
              <p className="text-xs text-neutral-400 mt-2">{a.category} · {a.views || 0} views{!a.published && " · DRAFT"}</p>
            </button>
          ))}
        </div>
      )}

      {view && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4 overflow-y-auto" data-testid="kb-view-modal" onClick={() => setView(null)}>
          <div className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-2xl w-full p-8 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-xl font-bold font-heading">{view.title}</h2>
              <button onClick={() => setView(null)} className="text-neutral-400 hover:text-neutral-900" data-testid="kb-view-close">✕</button>
            </div>
            <p className="text-xs text-neutral-400 mt-1 mb-4">{view.category} · by {view.author_name} · updated {new Date(view.updated_at).toLocaleDateString()}</p>
            <p className="text-sm text-neutral-700 whitespace-pre-wrap leading-relaxed">{view.body}</p>
            {isAgent && (
              <div className="flex gap-2 mt-6 pt-4 border-t border-neutral-100">
                <button data-testid="kb-edit-button" onClick={() => { setEditor(view); setView(null); }}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 border border-neutral-300 rounded-md text-xs font-medium hover:bg-neutral-50"><Pencil size={12} /> Edit</button>
                <button data-testid="kb-delete-button" onClick={() => remove(view)}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 border border-red-200 text-red-700 rounded-md text-xs font-medium hover:bg-red-50"><Trash2 size={12} /> Delete</button>
              </div>
            )}
          </div>
        </div>
      )}

      {editor && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4 overflow-y-auto" data-testid="kb-editor-modal">
          <form onSubmit={save} className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-2xl w-full p-8 my-8">
            <h3 className="font-semibold font-heading text-lg mb-4">{editor.id ? "Edit article" : "New article"}</h3>
            <label className="text-sm font-medium text-neutral-700">Title</label>
            <input data-testid="kb-title-input" required minLength={3} value={editor.title}
              onChange={(e) => setEditor((f) => ({ ...f, title: e.target.value }))} className={inputCls + " w-full mt-1"} />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div><label className="text-sm font-medium text-neutral-700">Category</label>
                <select data-testid="kb-editor-category" value={editor.category} onChange={(e) => setEditor((f) => ({ ...f, category: e.target.value }))} className={inputCls + " w-full mt-1"}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <label className="text-sm text-neutral-600 flex items-end gap-2 pb-2">
                <input data-testid="kb-published-checkbox" type="checkbox" checked={editor.published}
                  onChange={(e) => setEditor((f) => ({ ...f, published: e.target.checked }))} /> Published (visible to end users)
              </label>
            </div>
            <label className="text-sm font-medium text-neutral-700 mt-3 block">Content</label>
            <textarea data-testid="kb-body-input" required rows={10} value={editor.body}
              onChange={(e) => setEditor((f) => ({ ...f, body: e.target.value }))} className={inputCls + " w-full mt-1"} />
            <div className="flex gap-2 mt-5">
              <button data-testid="kb-save-button" disabled={busy}
                className="flex-1 py-2.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
                {busy ? "Saving..." : "Save article"}
              </button>
              <button type="button" onClick={() => setEditor(null)} data-testid="kb-editor-cancel"
                className="px-5 py-2.5 rounded-md border border-neutral-300 text-sm font-medium hover:bg-neutral-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
